import { CircularProgress, MenuItem, useTheme } from '@mui/material';
import type { FC } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Iconify, TableMoreMenu } from '@pflegenavi/web-components';
import { useFormatting } from '@pflegenavi/frontend/localization';
import {
  pflegenaviBase64Logo,
  usePrintPdfWithdrawalWithoutResidentConfirmation,
} from '@pflegenavi/frontend/pdf-export';
import { enqueueSnackbar } from 'notistack';
import { useNursingHomeContext } from '@pflegenavi/frontend/nursing-home-context';
import type { TransformedCashTransactionGroup } from '@pflegenavi/frontend/api-nursing-home';
import {
  useCashListConfiguration,
  useEmployeeProfilePhoenix,
  useImage,
} from '@pflegenavi/frontend/api-nursing-home';
import { CoinsModal } from '../modals/CoinsModal';
import { format } from 'date-fns';
import { makeSafeFilename } from '@pflegenavi/shared/utils';

export type CashTransactionRow = NonNullable<
  TransformedCashTransactionGroup['cashListTransactions']
>[number];

interface CashTransactionRowActionsProps {
  transaction: CashTransactionRow;
  showDownloadPdf: boolean;
}

export const CashTransactionRowActions: FC<CashTransactionRowActionsProps> = ({
  transaction: {
    date,
    amount,
    separateFeeAmount,
    immediateFeeAmount,
    notes,
    coinChange,
    bankAccountAmountChange,
    cashListId,
  },
  showDownloadPdf,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { fCurrencyCents } = useFormatting();

  const { data: user } = useEmployeeProfilePhoenix({});
  const { selectedNursingHome } = useNursingHomeContext();
  const { data: pdfLogo } = useImage(selectedNursingHome?.pdfLogoId);
  const { cashLists } = useCashListConfiguration();

  const [showWithdrawalPdfLoading, setShowWithdrawalPdfLoading] =
    useState(false);

  const { handlePrintPdfWithdrawalWithoutResidentConfirmation } =
    usePrintPdfWithdrawalWithoutResidentConfirmation();

  const showWithdrawalPdf = async () => {
    try {
      if (!selectedNursingHome) {
        throw new Error('No selectedNursingHome');
      }

      setShowWithdrawalPdfLoading(true);
      const pdfPromise = handlePrintPdfWithdrawalWithoutResidentConfirmation({
        withdrawalAmount: amount / 100,
        date: date,
        employee: user ? `${user.user.first_name} ${user.user.last_name}` : '',
        nursingHome: selectedNursingHome?.name,
        logo: pdfLogo ? pdfLogo.url : pflegenaviBase64Logo,
        notes: notes,
        cashListName: cashLists.find((cashList) => cashList.id === cashListId)
          ?.name,
      });

      const minTime = new Promise((resolve) => setTimeout(resolve, 500));
      const [pdf] = await Promise.all([pdfPromise, minTime]);

      const dateString = format(date, 'yyyy-MM-dd');

      pdf.save(
        makeSafeFilename(
          t('transactions.withdrawal.pdfName-no-resident', {
            date: dateString,
          })
        )
      );
    } catch {
      enqueueSnackbar(t('errors.something-went-wrong'), {
        variant: 'error',
      });
    } finally {
      setShowWithdrawalPdfLoading(false);
    }
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState<HTMLElement | null>(null);

  return (
    <>
      <TableMoreMenu
        open={menuOpen}
        onOpen={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setMenuOpen(e.currentTarget);
        }}
        onClose={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setMenuOpen(null);
        }}
        actions={
          <>
            <MenuItem
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setModalOpen(true);
                setMenuOpen(null);
              }}
            >
              <Iconify icon={'eva:eye-fill'} />
              {t(
                'cashManagement.group-detail.cash-transaction-card.show-details'
              )}
            </MenuItem>
            <MenuItem
              disabled={!showDownloadPdf || showWithdrawalPdfLoading}
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await showWithdrawalPdf();
                setMenuOpen(null);
              }}
            >
              {showWithdrawalPdfLoading ? (
                <CircularProgress size={18} sx={{ color: 'grey.400' }} />
              ) : (
                <Iconify
                  icon="ph:download-simple-bold"
                  color={theme.palette.primary.main}
                />
              )}
              {t(
                'cashManagement.group-detail.cash-transaction-card.download-pdf'
              )}
            </MenuItem>
          </>
        }
      />
      <CoinsModal
        open={modalOpen}
        setOpen={setModalOpen}
        coins={coinChange}
        bankAccountAmount={bankAccountAmountChange ?? 0}
        amountColor={
          amount < 0
            ? 'error.main'
            : amount > 0
            ? 'success.main'
            : theme.palette.grey['400']
        }
        amountString={fCurrencyCents(amount)}
        separateFeeAmountString={fCurrencyCents(separateFeeAmount)}
        separateFeeAmountColor={
          separateFeeAmount !== 0 ? 'warning.main' : theme.palette.grey['400']
        }
        immediateFeeAmountString={fCurrencyCents(immediateFeeAmount)}
        immediateFeeAmountColor={theme.palette.grey['400']}
        selectedCashListName={undefined}
      />
    </>
  );
};
